import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'react-hot-toast';
import { fetchWithAuth, api } from '@/services/api';
import { Conversation } from '@/types';
import { useLojaAtivaStore } from './lojaAtivaStore';
import { useLeadsStore } from './leadsStore';
import { queryClient } from '@/lib/query/queryClient';
import {
  buildConversationsQueryKey,
  recoverConversations,
  getConsolidatedConversations,
  updateScopedConversationCache,
  captureConversationArchiveRollback,
  removeLeadConversationsFromCache,
  UseConversationsOptions
} from '@/lib/query/useConversations';
import { InfiniteData } from '@tanstack/react-query';
import { ConversationsPageResponse } from '@/services/api';

export type { Conversation } from '@/types';

let fetchConversationsRequestId = 0;
let inFlightConvsKey: string | null = null;
let inFlightConvsPromise: Promise<void> | null = null;

interface ConversationsState {
  conversations: Conversation[];
  activeConversationId: string | null;
  loading: boolean;
  loadingMore: boolean;
  fetched: boolean;
  totalCount: number;
  hasMore: boolean;
  showArchived: boolean;
  syncError: Error | null;
  lastFilters: {
    tenant_id?: string;
    connection_id?: string;
    archived?: boolean;
    loja_id?: string;
    transbordo?: boolean;
    my_only?: boolean;
    q?: string;
  } | null;
  setShowArchived: (show: boolean) => void;
  fetchConversations: (filters?: {
    tenant_id?: string;
    connection_id?: string;
    archived?: boolean;
    loja_id?: string;
    transbordo?: boolean;
    my_only?: boolean;
    q?: string;
    silent?: boolean;
    offset?: number;
    limit?: number;
  }) => Promise<void>;
  fetchNextPage: () => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  updateStatus: (id: string, status: 'open' | 'resolved' | 'pending' | 'snoozed') => Promise<void>;
  updatePriority: (id: string, priority: 'low' | 'medium' | 'high' | 'urgent') => Promise<void>;
  resolveConversation: (id: string) => Promise<void>;
  archiveConversation: (id: string, archived: boolean) => Promise<void>;
  updateLocally: (id: string, data: Partial<Conversation>) => void;
  removeLeadConversations: (leadId: string) => void;
}

function syncQueryClientToStore(
  options: UseConversationsOptions,
  set: (fn: (state: ConversationsState) => Partial<ConversationsState>) => void
) {
  const queryKey = buildConversationsQueryKey(options);
  const storageKey = JSON.stringify(queryKey);
  const cached = queryClient.getQueryData<InfiniteData<ConversationsPageResponse>>(queryKey);
  const consolidated = getConsolidatedConversations(storageKey, cached?.pages);
  const lastPage = cached?.pages?.[cached.pages.length - 1];

  set(state => ({
    conversations: consolidated,
    hasMore: Boolean(lastPage?.hasMore),
    totalCount: consolidated.length,
    fetched: true
  }));
}

export const useConversationsStore = create<ConversationsState>()(
  persist(
    (set, get) => ({
      conversations: [],
      activeConversationId: null,
      loading: false,
      loadingMore: false,
      fetched: false,
      totalCount: 0,
      hasMore: false,
      showArchived: false,
      syncError: null,
      lastFilters: null,

      setShowArchived: (show: boolean) => {
        set({ showArchived: show });
      },

      fetchConversations: async (filters) => {
        let activeFilters: any;
        if (filters !== undefined) {
          const isTabChange = (filters.my_only !== undefined || filters.transbordo !== undefined);
          if (isTabChange) {
            activeFilters = filters;
          } else if ((filters as any).silent && get().lastFilters) {
            activeFilters = { ...get().lastFilters, ...filters };
          } else {
            activeFilters = filters;
          }
          set({ lastFilters: activeFilters });
        } else if (get().lastFilters) {
          activeFilters = get().lastFilters;
        } else {
          const activeLojaId = useLojaAtivaStore.getState().lojaAtivaId;
          if (activeLojaId) {
            activeFilters = { loja_id: activeLojaId };
          }
        }

        const queryOpts: UseConversationsOptions = {
          tenantId: activeFilters?.tenant_id || undefined,
          lojaId: activeFilters?.loja_id || undefined,
          connectionId: activeFilters?.connection_id || undefined,
          archived: activeFilters?.archived !== undefined ? Boolean(activeFilters.archived) : get().showArchived,
          transbordo: Boolean(activeFilters?.transbordo),
          myOnly: Boolean(activeFilters?.my_only),
          q: activeFilters?.q || undefined
        };

        const dedupKey = JSON.stringify(queryOpts);
        if (inFlightConvsPromise && inFlightConvsKey === dedupKey) {
          return inFlightConvsPromise;
        }

        const myRequestId = ++fetchConversationsRequestId;
        const runFetch = (async () => {
          const isSilent = (filters as any)?.silent || (get().fetched && !activeFilters?.q);
          if (!isSilent) {
            set({ loading: true });
          }
          try {
            const recovered = await recoverConversations(queryOpts, queryClient);
            if (myRequestId !== fetchConversationsRequestId) return;

            const queryKey = buildConversationsQueryKey(queryOpts);
            const storageKey = JSON.stringify(queryKey);
            const consolidated = getConsolidatedConversations(storageKey, recovered?.pages);
            const lastPage = recovered?.pages?.[recovered.pages.length - 1];

            set({
              conversations: consolidated,
              totalCount: consolidated.length,
              hasMore: Boolean(lastPage?.hasMore),
              fetched: true,
              syncError: null
            });
          } catch (err: any) {
            if (err?.name === 'AbortError') {
              console.log('[CONVERSATIONS_STORE] Fetch abortado');
            } else {
              console.error('[CONVERSATIONS_STORE] Exception:', err);
              set({ syncError: err });
            }
          } finally {
            set({ loading: false, fetched: true });
            inFlightConvsPromise = null;
            inFlightConvsKey = null;
          }
        })();

        inFlightConvsKey = dedupKey;
        inFlightConvsPromise = runFetch;
        return runFetch;
      },

      fetchNextPage: async () => {
        const { lastFilters, loading, loadingMore, hasMore } = get();
        if (loading || loadingMore || !hasMore) return;

        const queryOpts: UseConversationsOptions = {
          tenantId: lastFilters?.tenant_id || undefined,
          lojaId: lastFilters?.loja_id || undefined,
          connectionId: lastFilters?.connection_id || undefined,
          archived: lastFilters?.archived !== undefined ? Boolean(lastFilters.archived) : get().showArchived,
          transbordo: Boolean(lastFilters?.transbordo),
          myOnly: Boolean(lastFilters?.my_only),
          q: lastFilters?.q || undefined
        };

        const queryKey = buildConversationsQueryKey(queryOpts);
        const currentData = queryClient.getQueryData<InfiniteData<ConversationsPageResponse>>(queryKey);
        const lastPage = currentData?.pages?.[currentData.pages.length - 1];

        if (!lastPage || !lastPage.hasMore || !lastPage.nextCursor) {
          set({ hasMore: false });
          return;
        }

        set({ loadingMore: true });
        try {
          const nextPageRes = await api.getConversationsPage({
            cursor: lastPage.nextCursor,
            limit: 50,
            loja_id: queryOpts.lojaId,
            tenant_id: queryOpts.tenantId,
            connection_id: queryOpts.connectionId,
            archived: queryOpts.archived,
            transbordo: queryOpts.transbordo,
            my_only: queryOpts.myOnly,
            q: queryOpts.q
          });

          queryClient.setQueryData<InfiniteData<ConversationsPageResponse>>(queryKey, (old) => {
            if (!old) {
              return {
                pages: [nextPageRes],
                pageParams: [null]
              };
            }
            return {
              pages: [...old.pages, nextPageRes],
              pageParams: [...old.pageParams, lastPage.nextCursor]
            };
          });

          syncQueryClientToStore(queryOpts, set);
        } catch (err: any) {
          console.error('[CONVERSATIONS_STORE] Erro no fetchNextPage:', err);
        } finally {
          set({ loadingMore: false });
        }
      },

      selectConversation: async (id: string) => {
        set({ activeConversationId: id });
        get().updateLocally(id, { unread_count: 0 });

        try {
          await fetchWithAuth(`/api/conversations/${id}/read`, { method: 'POST' });
        } catch (err) {
          console.warn('[CONVERSATIONS_STORE] Error marking as read:', err);
        }
      },

      updateStatus: async (id: string, status) => {
        get().updateLocally(id, { status });
        try {
          await fetchWithAuth(`/api/conversations/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status })
          });
        } catch (err) {
          console.error('[CONVERSATIONS_STORE] Error updating status:', err);
        }
      },

      updatePriority: async (id: string, priority) => {
        get().updateLocally(id, { priority });
        try {
          await fetchWithAuth(`/api/conversations/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ priority })
          });
        } catch (err) {
          console.error('[CONVERSATIONS_STORE] Error updating priority:', err);
        }
      },

      resolveConversation: async (id: string) => {
        get().updateLocally(id, { status: 'resolved' });
        try {
          await fetchWithAuth(`/api/conversations/${id}/resolve`, { method: 'POST' });
        } catch (err) {
          console.error('[CONVERSATIONS_STORE] Error resolving conversation:', err);
        }
      },

      archiveConversation: async (id: string, archived: boolean) => {
        const conv = get().conversations.find(c => c.id === id || c.lead_id === id);
        // Materialize legacy admitted rows before taking operation-local snapshots.
        get().updateLocally(id, {});
        const restoreArchive = captureConversationArchiveRollback(id);
        const rollback = () => {
          restoreArchive();
          // Re-consolidate the current scope; never apply an old ID to a new context.
          get().updateLocally('', {});
        };
        const targetLeadId = conv?.lead_id || id;
        try {
          get().updateLocally(id, { is_archived: archived });
          if (targetLeadId) {
            useLeadsStore.getState().updateLeadLocally(targetLeadId, { is_archived: archived });
          }
          const res = await fetchWithAuth(`/api/conversations/${id}/archive`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ archived })
          });

          if (!res.ok) {
            rollback();
            if (targetLeadId) {
              useLeadsStore.getState().updateLeadLocally(targetLeadId, { is_archived: !archived });
            }
            toast.error('Erro ao arquivar conversa. Tente novamente.');
          } else {
            const data = await res.json().catch(() => ({}));
            const savedConv = data?.conversation || data;
            if (savedConv && typeof savedConv === 'object' && savedConv.id) {
              get().updateLocally(id, { ...savedConv, is_archived: archived });
            }
            // A destination filter may never have admitted this ID (notably unarchive).
            // Revalidate through its server predicate instead of copying it across scopes.
            await queryClient.invalidateQueries({ queryKey: ['conversations'], refetchType: 'none' });
            await Promise.all(queryClient.getQueryCache().findAll({ queryKey: ['conversations'] })
              .filter(query => query.isActive())
              .map(query => recoverConversations(query.queryKey[1] as UseConversationsOptions, queryClient)
                .catch(error => console.warn('[CONVERSATIONS_STORE] Archive revalidation failed:', error))));
            toast.success(archived ? 'Conversa arquivada' : 'Conversa desarquivada');
          }
        } catch (err) {
          rollback();
          if (targetLeadId) {
            useLeadsStore.getState().updateLeadLocally(targetLeadId, { is_archived: !archived });
          }
          toast.error('Erro de conexão ao arquivar.');
        } finally {
          restoreArchive.release();
        }
      },

      updateLocally: (id: string, data: Partial<Conversation>) => {
        const state = get();
        const filters = state.lastFilters;
        const queryKey = buildConversationsQueryKey({
          lojaId: filters?.loja_id || useLojaAtivaStore.getState().lojaAtivaId || undefined,
          tenantId: filters?.tenant_id,
          connectionId: filters?.connection_id,
          archived: filters?.archived ?? state.showArchived,
          transbordo: filters?.transbordo,
          myOnly: filters?.my_only,
          q: filters?.q
        });
        // Legacy store rows are already server-admitted; preserve them in the same canonical cache.
        if (!queryClient.getQueryData(queryKey) && state.conversations.length) {
          queryClient.setQueryData(queryKey, { pages: [{ items: state.conversations, hasMore: state.hasMore, nextCursor: null }], pageParams: [null] });
        }
        for (const query of queryClient.getQueryCache().findAll({ queryKey: ['conversations'] })) {
          const cached = query.state.data as InfiniteData<ConversationsPageResponse> | undefined;
          const matched = cached?.pages.flatMap(p => p.items).filter(c => c.id === id || c.lead_id === id) || [];
          if (matched.length) {
            for (const conv of matched) updateScopedConversationCache(query.queryKey, conv.id, data);
          } else {
            // Only the canonical helper can recover a previously admitted, now hidden row.
            updateScopedConversationCache(query.queryKey, id, data);
          }
        }
        const cached = queryClient.getQueryData<InfiniteData<ConversationsPageResponse>>(queryKey);
        const conversations = getConsolidatedConversations(JSON.stringify(queryKey), cached?.pages);
        set({ conversations, totalCount: conversations.length });
      },

      removeLeadConversations: (leadId: string) => {
        removeLeadConversationsFromCache(leadId);
        set(state => {
          const conversations = state.conversations.filter(c => c.lead_id !== leadId);
          const activeGone = !conversations.some(c => c.id === state.activeConversationId);
          return { conversations, totalCount: conversations.length, activeConversationId: activeGone ? null : state.activeConversationId };
        });
      }
    }),
    {
      name: 'task-crm-conversations-ui',
      partialize: (state) => ({
        showArchived: state.showArchived
      })
    }
  )
);
